import React, { useState, useEffect, useRef } from 'react';

export default function EventsFeed({ 
  events, 
  getPlayerColor, 
  isChatExpanded, 
  isEventsExpanded, 
  setIsEventsExpanded,
  onAcceptTradeAgreement,
  onRejectTradeAgreement,
  containerStyle = {}
}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [handledAgreementIds, setHandledAgreementIds] = useState([]); // New state to track handled trade agreement IDs
  const [eventFilter, setEventFilter] = useState('all'); // all, personal, public
  const lastEventsCountRef = useRef(events.length);

  // Track new events when feed is collapsed
  useEffect(() => {
    if (events.length > lastEventsCountRef.current && !isEventsExpanded) {
      setUnreadCount(prev => prev + (events.length - lastEventsCountRef.current));
    }
    lastEventsCountRef.current = events.length;
  }, [events, isEventsExpanded]);

  // Reset unread count when feed is expanded
  useEffect(() => {
    if (isEventsExpanded) {
      setUnreadCount(0);
    }
  }, [isEventsExpanded]);

  const getTileTypeText = (tileType) => {
    const tileTypeMap = {
      3: 'Farm', 4: 'Market', 5: 'Mine', 6: 'Fishery', 7: 'Port',
      8: 'Oil Rig', 9: 'Oil Well', 10: 'Lumber Mill', 11: 'Power Plant',
      12: 'Factory', 13: 'Bank', 14: 'Construction Company'
    };
    return tileTypeMap[tileType] || 'Unknown';
  };

  const getTileTypeIcon = (tileType) => {
    const iconMap = {
      3: '🌾', 4: '🏪', 5: '⛏️', 6: '🐟', 7: '⚓', 8: '🛢️', 9: '🕳️',
      10: '🏗️', 11: '⚡', 12: '🏭', 13: '🏦', 14: '🏢'
    };
    return iconMap[tileType] || '📍';
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    
    if (diff < 60000) {
      return 'now';
    } else if (diff < 3600000) {
      return `${Math.floor(diff / 60000)}m ago`;
    } else {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const handleAccept = (agreementId) => {
    onAcceptTradeAgreement(agreementId);
    setHandledAgreementIds(prev => [...prev, agreementId]); // Mark as handled
  };

  const handleReject = (agreementId) => {
    onRejectTradeAgreement(agreementId);
    setHandledAgreementIds(prev => [...prev, agreementId]); // Mark as handled
  };

  return (
    <div style={{
      position: 'absolute',
      bottom: isChatExpanded ? '320px' : '60px', // Position just above chat (320px for expanded, 60px for collapsed)
      left: '10px',
      background: 'rgba(0,0,0,0.9)',
      color: 'white',
      borderRadius: '8px',
      fontFamily: 'Arial, sans-serif',
      width: '320px',
      height: isEventsExpanded ? '200px' : '40px', // Adjust height based on expansion state
      border: '2px solid #FF9800',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'all 0.3s ease', // Add smooth vertical animation
      ...containerStyle
    }}>
      {/* Events Header */}
      <div 
        onClick={() => setIsEventsExpanded(!isEventsExpanded)}
        style={{
          padding: '10px 15px',
          backgroundColor: 'rgba(255, 152, 0, 0.8)',
          cursor: 'pointer',
          borderRadius: '6px 6px 0 0',
          fontSize: '0.9em',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: isEventsExpanded ? '1px solid rgba(255,255,255,0.2)' : 'none'
        }}
      >
        <span>📢 Events{unreadCount > 0 && ` (${unreadCount})`}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isEventsExpanded && (
            <select 
              value={eventFilter}
              onChange={(e) => {
                e.stopPropagation();
                setEventFilter(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'rgba(0,0,0,0.5)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '4px',
                fontSize: '0.8em',
                padding: '2px 4px',
                cursor: 'pointer'
              }}
            >
              <option value="all">All</option>
              <option value="personal">Personal</option>
              <option value="public">Public</option>
            </select>
          )}
          <span style={{ fontSize: '0.8em' }}>{isEventsExpanded ? '▼' : '▲'}</span>
        </div>
      </div>

      {/* Events Content - Only visible when expanded */}
      {isEventsExpanded && (
        <div style={{
          flex: 1,
          padding: '10px',
          overflowY: 'auto',
          fontSize: '0.8em',
          maxHeight: '150px'
        }}>
          {events.length === 0 ? (
            <div style={{ 
              color: 'rgba(255, 255, 255, 0.6)', 
              fontStyle: 'italic',
              textAlign: 'center',
              marginTop: '20px'
            }}>
              No events yet...
            </div>
          ) : (
            events
              .filter(event => {
                if (event.type === 'trade_agreement_request' && handledAgreementIds.includes(event.agreementId)) return false;
                if (eventFilter === 'personal') return event.isPersonal;
                if (eventFilter === 'public') return !event.isPersonal;
                return true;
              })
              .slice(-20).reverse().map((event) => (
              <div key={event.id} style={{
                marginBottom: '8px',
                padding: '6px 8px',
                backgroundColor: event.type === 'auction_start' ? 'rgba(33, 150, 243, 0.15)' : 
                                event.type === 'operating_cost' ? 'rgba(255, 87, 34, 0.15)' : 
                                event.type === 'route_income' ? 'rgba(76, 175, 80, 0.15)' :
                                event.type === 'trade_agreement_request' ? 'rgba(156, 39, 176, 0.15)' :
                                event.type === 'property_sold' ? 'rgba(255, 152, 0, 0.15)' :
                                'rgba(76, 175, 80, 0.15)',
                borderRadius: '4px',
                borderLeft: `3px solid ${event.type === 'auction_start' ? '#2196F3' : 
                                        event.type === 'operating_cost' ? '#FF5722' : 
                                        event.type === 'route_income' ? '#4CAF50' :
                                        event.type === 'trade_agreement_request' ? '#9C27B0' :
                                        event.type === 'property_sold' ? '#FF9800' :
                                        '#4CAF50'}`,
                wordWrap: 'break-word'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '4px'
                }}>
                  <div style={{ flex: 1 }}>
                    {event.type === 'auction_start' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🔨</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> started auction for </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {getTileTypeIcon(event.tileType)} {getTileTypeText(event.tileType)}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Starting bid: ${event.startingBid?.toLocaleString() || '10,000'}
                        </div>
                      </div>
                    )}
                    
                    {event.type === 'auction_win' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🏆</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> won </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {getTileTypeIcon(event.tileType)} {getTileTypeText(event.tileType)}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Winning bid: ${event.winningBid?.toLocaleString() || '0'}
                        </div>
                      </div>
                    )}

                    {event.type === 'operating_cost' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💸</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          You paid 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#FF5722', marginLeft: '4px' }}>
                          ${event.operatingCostAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> in operating costs</span>
                      </div>
                    )}

                    {event.type === 'loan_request' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💰</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          You requested a loan of 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#4CAF50', marginLeft: '4px' }}>
                          ${event.loanAmount?.toLocaleString() || '0'}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Interest rate: {event.interestRate}% per minute
                        </div>
                      </div>
                    )}

                    {event.type === 'loan_granted' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🏦</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          Your bank granted a loan of 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#4CAF50', marginLeft: '4px' }}>
                          ${event.loanAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> to </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {event.borrowerName}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Interest rate: {event.interestRate}% per minute
                        </div>
                      </div>
                    )}

                    {event.type === 'PlayerAction' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>⚔️</span>
                        <span style={{ fontWeight: 'bold', color: getPlayerColor(event.playerId), marginLeft: '4px' }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> attacked </span>
                        <span style={{ fontWeight: 'bold', color: getPlayerColor(event.targetPlayerId) || '#FFD700' }}>
                          {event.targetName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> with </span>
                        <span style={{ fontWeight: 'bold', color: '#FF6B6B' }}>
                          {event.reason === 'criticise' ? 'Criticise' : event.reason === 'fakeNews' ? 'Fake News' : event.reason === 'sabotage' ? 'Sabotage' : event.reason}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> for {event.durationMinutes} minutes!</span>
                      </div>
                    )}
                    
                    {event.type === 'PlayerDebuffed' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🚨</span>
                        <span style={{ fontWeight: 'bold', color: '#FF6B6B' }}>
                          You are being attacked with {event.reason === 'criticise' ? 'Criticise' : event.reason === 'fakeNews' ? 'Fake News' : event.reason === 'sabotage' ? 'Sabotage' : event.reason} for {event.durationMinutes} minutes! Your income will be reduced.
                        </span>
                      </div>
                    )}

                    {event.type === 'loan_repayment' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💳</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          You repaid 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#2196F3', marginLeft: '4px' }}>
                          ${event.repayAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> on your loan</span>
                      </div>
                    )}

                    {event.type === 'loan_payment_received' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💰</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          Received loan payment of 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#4CAF50', marginLeft: '4px' }}>
                          ${event.repayAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> from </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {event.borrowerName}
                        </span>
                      </div>
                    )}

                    {event.type === 'loan_interest_charge' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>📊</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          Interest charged: 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#FF9800', marginLeft: '4px' }}>
                          ${event.interestAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> on your loan</span>
                      </div>
                    )}

                    {event.type === 'loan_interest_income' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💰</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          Interest income: 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#4CAF50', marginLeft: '4px' }}>
                          ${event.interestAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> from </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {event.borrowerName}
                        </span>
                      </div>
                    )}

                    {event.type === 'loan_auto_collection' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>⚠️</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          Loan auto-collected: 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#F44336', marginLeft: '4px' }}>
                          ${event.collectedAmount?.toLocaleString() || '0'}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Reason: {event.reason === 'expired' ? 'Loan expired' : 'Insufficient funds for interest'}
                        </div>
                      </div>
                    )}

                    {event.type === 'route_income' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💰</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          You earned 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#4CAF50', marginLeft: '4px' }}>
                          ${event.routeIncomeAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> from connected routes</span>
                      </div>
                    )}

                    {event.type === 'loan_collected' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🏦</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          Loan collected: 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#4CAF50', marginLeft: '4px' }}>
                          ${event.collectedAmount?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> from </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {event.borrowerName}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Reason: {event.reason === 'expired' ? 'Loan expired' : 'Insufficient funds for interest'}
                        </div>
                      </div>
                    )}

                    {event.type === 'tile_upgrade' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>📦</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> upgraded routes at </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {getTileTypeIcon(event.tileType)} {getTileTypeText(event.tileType)}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> to level {event.upgradeLevel}</span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Cost: ${event.upgradeCost?.toLocaleString() || '0'}
                        </div>
                      </div>
                    )}

                    {event.type === 'equipment_upgrade' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>⚙️</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> upgraded equipment at </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {getTileTypeIcon(event.tileType)} {getTileTypeText(event.tileType)}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> to level {event.upgradeLevel}</span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Cost: ${event.upgradeCost?.toLocaleString() || '0'}
                        </div>
                      </div>
                    )}

                    {event.type === 'trade_agreement_request' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🤝</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> sent you a trade request</span>
                        
                        <div style={{ 
                          marginTop: '6px', 
                          display: 'flex', 
                          gap: '6px'
                        }}>
                          <button
                            onClick={() => handleAccept(event.agreementId)} // Use new handler
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75em',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleReject(event.agreementId)} // Use new handler
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#F44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75em',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    )}

                    {event.type === 'trade_agreement_accepted' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>✅</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> accepted your trade request</span>
                      </div>
                    )}

                    {event.type === 'trade_agreement_active' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🤝</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          Trade agreement active with 
                        </span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: '#FFD700',
                          marginLeft: '4px'
                        }}>
                          {event.partnerName}
                        </span>
                      </div>
                    )}

                    {event.type === 'trade_agreement_rejected' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>❌</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: '#FFD700',
                          marginLeft: '4px'
                        }}>
                          {event.rejectorName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> rejected your trade request</span>
                      </div>
                    )}

                    {event.type === 'trade_agreement_cancelled' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🚫</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: '#FFD700',
                          marginLeft: '4px'
                        }}>
                          {event.cancellerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> cancelled your trade agreement</span>
                      </div>
                    )}
                    
                    {event.type === 'trade_agreement_established' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🤝</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> and </span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.targetId)
                        }}>
                          {event.targetName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> formed a trade agreement!</span>
                      </div>
                    )}

                    {event.type === 'stock_purchase' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>📈</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> bought stock of </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {event.targetName}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Price: ${event.stockPrice?.toLocaleString() || '0'} • Total: {event.stockCount || 0} stock{event.stockCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}

                    {event.type === 'stock_buyback' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>↩️</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> bought back stock from </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {event.acquirerName}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Price: ${event.buybackPrice?.toLocaleString() || '0'} • Remaining: {event.remainingStocks || 0} stock{event.remainingStocks !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}

                    {event.type === 'takeover' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🏢</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> took over </span>
                        <span style={{ fontWeight: 'bold', color: '#FF4444' }}>
                          {event.targetName}
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          All assets transferred!
                        </div>
                      </div>
                    )}

                    {event.type === 'property_sold' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💸</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px' }}>
                          You sold 
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700', marginLeft: '4px' }}>
                          {getTileTypeIcon(event.tileType)} {getTileTypeText(event.tileType)}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> for </span>
                        <span style={{ fontWeight: 'bold', color: '#FF9800', marginLeft: '4px' }}>
                          ${event.sellPrice?.toLocaleString() || '0'}
                        </span>
                      </div>
                    )}

                    {event.type === 'stock_trading_enabled' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>📊</span>
                        <span style={{ color: 'rgba(255,255,255,0.9)', marginLeft: '4px', fontWeight: 'bold' }}>
                          Stock trading is now available!
                        </span>
                      </div>
                    )}

                    {event.type === 'economy_effect_started' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>
                          {event.effectType === 'increase' ? '📈' : '📉'}
                        </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700', marginLeft: '4px' }}>
                          {getTileTypeIcon(event.tileType)} {event.tileTypeName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}>
                          {' '}income {event.effectType === 'increase' ? 'increased' : 'decreased'} by{' '}
                        </span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: event.effectType === 'increase' ? '#4CAF50' : '#FF5722' 
                        }}>
                          {event.effectPercentage}%
                        </span>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                          Duration: {event.durationMinutes} minutes
                        </div>
                      </div>
                    )}

                    {event.type === 'economy_effect_ended' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>⏰</span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700', marginLeft: '4px' }}>
                          {getTileTypeIcon(event.tileType)} {event.tileTypeName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}>
                          {' '}economy effect has ended
                        </span>
                      </div>
                    )}

                    {event.type === 'frog_spawned' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🐸</span>
                        <span style={{ fontWeight: 'bold', color: '#8FBC8F', marginLeft: '4px' }}>
                          Prog the Frog
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> has spawned!</span>
                      </div>
                    )}

                    {event.type === 'frog_arrived' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🐸</span>
                        <span style={{ fontWeight: 'bold', color: '#8FBC8F', marginLeft: '4px' }}>
                          Prog the Frog
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> arrived at </span>
                        <span style={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {getTileTypeIcon(event.tileType)} {getTileTypeText(event.tileType)}
                        </span>
                      </div>
                    )}

                    {event.type === 'frog_disappeared' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>🐸</span>
                        <span style={{ fontWeight: 'bold', color: '#8FBC8F', marginLeft: '4px' }}>
                          Prog the Frog
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> has disappeared!</span>
                      </div>
                    )}

                    {event.type === 'bankruptcy' && (
                      <div style={{ lineHeight: '1.3' }}>
                        <span style={{ fontSize: '1.1em' }}>💸</span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getPlayerColor(event.playerId),
                          marginLeft: '4px'
                        }}>
                          {event.playerName}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.9)' }}> went bankrupt and was eliminated!</span>
                      </div>
                    )}
                  </div>
                  
                  <span style={{ 
                    fontSize: '0.7em', 
                    color: 'rgba(255,255,255,0.5)',
                    marginLeft: '8px',
                    flexShrink: 0
                  }}>
                    {formatTimestamp(event.timestamp)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}